import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { useT } from "@/i18n/I18nContext";
import { api, resolvePhoto } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CascadeSelect } from "@/components/CascadeSelect";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ShieldCheck, Tractor, ShoppingBasket, Check, X, Phone, Clock, Camera, FileText, MessageCircle, Eye, EyeOff } from "lucide-react";

const ACTIVITY_TYPES = ["Restaurant", "Traiteur", "Hôtel", "Boulangerie", "Épicerie", "Supermarché", "Grossiste", "Collectivité", "Autre"];
const FARM_TYPES = ["Élevage familial", "Ferme semi-industrielle", "Ferme industrielle", "Coopérative", "Autre"];
const FARM_SIZES = ["Petite (< 500 sujets)", "Moyenne (500 – 2000)", "Grande (> 2000)"];

const RoleButton = ({ active, Icon, label, hint, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`min-h-[150px] rounded-3xl border-2 p-6 flex flex-col items-center justify-center gap-3 text-center transition-colors active:scale-95 ${
      active ? "border-secondary bg-secondary/10 ring-4 ring-secondary/20" : "border-border bg-card hover:border-secondary/50"
    }`}>
    <Icon className={`h-16 w-16 ${active ? "text-secondary" : "text-primary"}`} strokeWidth={2} />
    <span className="font-heading font-extrabold text-xl text-secondary">{label}</span>
    <span className="text-sm text-muted-foreground">{hint}</span>
  </button>
);

const SpecButton = ({ active, Icon, label, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`min-h-[110px] rounded-2xl border-2 p-4 flex flex-col items-center justify-center gap-2 transition-colors active:scale-95 ${
      active ? "border-secondary bg-secondary/10" : "border-border bg-card hover:border-secondary/50"
    }`}>
    <Icon className={`h-11 w-11 ${active ? "text-secondary" : "text-primary"}`} strokeWidth={2} />
    <span className="font-semibold text-secondary text-base">{label}</span>
  </button>
);

// Vignette photo d'un produit du catalogue (sélection multiple à l'inscription vendeur)
const ProductButton = ({ active, image, label, onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`relative rounded-2xl border-2 overflow-hidden text-left transition-[border-color,box-shadow] active:scale-95 ${
      active ? "border-secondary ring-4 ring-secondary/20" : "border-border hover:border-secondary/50"
    }`}>
    <div className="aspect-[4/3] w-full overflow-hidden bg-muted">
      <img src={resolvePhoto(image)} alt={label} className="h-full w-full object-cover" loading="lazy" />
    </div>
    {active && (
      <span className="absolute top-2 right-2 h-7 w-7 rounded-full bg-secondary text-white flex items-center justify-center shadow-md" aria-hidden="true">
        <Check className="h-4 w-4" strokeWidth={3} />
      </span>
    )}
    <span className="block px-2.5 py-2 font-bold text-secondary text-sm text-center leading-tight">{label}</span>
  </button>
);

export default function Register() {
  const { register } = useAuth();
  const brand = useBrand();
  const { t } = useT();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", role: "buyer",
    country: "Mali", region: "", city: "", neighborhood: "", specialty: [],
    activity_type: "", profession: "", farm_type: "", farm_size: "", nif: "", documents: [],
  });
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [confirm, setConfirm] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleProduct = (slug) => setForm((f) => ({
    ...f,
    specialty: f.specialty.includes(slug) ? f.specialty.filter((s) => s !== slug) : [...f.specialty, slug],
  }));

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);

  const uploadDoc = async (file) => {
    setUploadingDoc(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, documents: [...f.documents, data.path] }));
    } catch { /* ignore */ } finally { setUploadingDoc(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.phone || form.phone.trim().length < 6) { setError(t("auth.phoneHint")); return; }
    if (!form.region || !form.neighborhood) { setError(t("common.required")); return; }
    if (form.role === "seller" && form.specialty.length === 0) { setError(t("auth.specRequired")); return; }
    if (form.password !== confirm) { setError(t("auth.passwordMismatch")); return; }
    setLoading(true);
    const payload = { ...form };
    if (!payload.email || !payload.email.trim()) delete payload.email;
    const res = await register(payload);
    setLoading(false);
    if (res.ok) navigate(res.user.role === "seller" ? "/vendeur" : "/acheteur");
    else setError(res.error);
  };

  return (
    <div className="container-page py-12 flex justify-center">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <span className="inline-flex h-16 w-16 rounded-2xl bg-white ring-1 ring-border items-center justify-center">
            <img src={brand.logoUrl} alt={brand.name} className="h-14 w-14 object-contain" />
          </span>
          <h1 className="mt-4 font-heading font-extrabold text-3xl sm:text-4xl text-secondary">{t("auth.registerTitle")}</h1>
          <p className="text-muted-foreground mt-1 text-base">🇲🇱 {brand.country || "Mali"}</p>
        </div>

        <form onSubmit={submit} className="rounded-3xl bg-card border-2 border-border p-6 sm:p-8 space-y-6" data-testid="register-form">
          {/* Big visual role selector */}
          <div>
            <Label className="text-lg font-bold text-secondary">{t("auth.chooseRole")}</Label>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <RoleButton active={form.role === "seller"} Icon={Tractor} label={t("auth.iSell")} hint={t("auth.iSellHint")} onClick={() => set("role", "seller")} testid="role-seller" />
              <RoleButton active={form.role === "buyer"} Icon={ShoppingBasket} label={t("auth.iBuy")} hint={t("auth.iBuyHint")} onClick={() => set("role", "buyer")} testid="role-buyer" />
            </div>
          </div>

          {/* Seller specialty — multi-select product photos */}
          {form.role === "seller" && (
            <div>
              <Label className="text-lg font-bold text-secondary">{t("auth.specialty")}</Label>
              <p className="text-sm text-muted-foreground mt-1">{t("auth.specMultiHint")}</p>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="seller-products">
                {categories.map((c) => (
                  <ProductButton
                    key={c.slug}
                    active={form.specialty.includes(c.slug)}
                    image={c.image}
                    label={t(`categories.${c.slug}`)}
                    onClick={() => toggleProduct(c.slug)}
                    testid={`spec-${c.slug}`}
                  />
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                <div>
                  <Label className="text-secondary font-semibold">{t("profile.farmType")}</Label>
                  <Select value={form.farm_type} onValueChange={(v) => set("farm_type", v)}>
                    <SelectTrigger className="mt-1.5 h-12" data-testid="register-farm-type"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{FARM_TYPES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-secondary font-semibold">{t("profile.farmSize")}</Label>
                  <Select value={form.farm_size} onValueChange={(v) => set("farm_size", v)}>
                    <SelectTrigger className="mt-1.5 h-12" data-testid="register-farm-size"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>{FARM_SIZES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {form.role === "buyer" && (
            <div>
              <Label className="text-secondary font-semibold">{t("profile.activityType")}</Label>
              <Select value={form.activity_type} onValueChange={(v) => set("activity_type", v)}>
                <SelectTrigger className="mt-1.5 h-12" data-testid="register-activity-type"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{ACTIVITY_TYPES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
              <div className="mt-4">
                <Label className="text-secondary font-semibold">{t("profile.profession")}</Label>
                <Input value={form.profession} onChange={(e) => set("profession", e.target.value)} className="mt-1.5 h-12" placeholder={t("profile.professionPlaceholder")} data-testid="register-profession" />
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-secondary font-semibold">{t("auth.name")}</Label>
              <Input required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 h-12" data-testid="register-name" />
            </div>
            <div>
              <Label className="flex items-center gap-1.5 text-secondary font-semibold"><Phone className="h-4 w-4" /> {t("auth.phone")}</Label>
              <Input required type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1.5 h-12" placeholder="+223 …" data-testid="register-phone" />
              <p className="text-xs text-muted-foreground mt-1">{t("auth.phoneHint")}</p>
            </div>
            <div>
              <Label className="text-secondary font-semibold">{t("auth.emailOptional")}</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 h-12" placeholder="—" data-testid="register-email" />
              <p className="text-xs text-muted-foreground mt-1">{t("auth.emailOptionalHint")}</p>
            </div>
            <div>
              <Label className="text-secondary font-semibold">{t("auth.password")}</Label>
              <div className="relative mt-1.5">
                <Input type={showPwd ? "text" : "password"} required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)} className="h-12 pr-11" data-testid="register-password" />
                <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-testid="toggle-password" aria-label="toggle">
                  {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-secondary font-semibold">{t("auth.confirmPassword")}</Label>
              <div className="relative mt-1.5">
                <Input type={showPwd ? "text" : "password"} required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-12 pr-11" data-testid="register-confirm-password" />
              </div>
              {confirm && confirm !== form.password && <p className="text-xs text-destructive mt-1" data-testid="password-mismatch">{t("auth.passwordMismatch")}</p>}
            </div>
          </div>

          {/* Mali cascade */}
          <CascadeSelect region={form.region} city={form.city} neighborhood={form.neighborhood} onRegion={(v) => set("region", v)} onCity={(v) => set("city", v)} onNeighborhood={(v) => set("neighborhood", v)} testid="register-cascade" />

          {form.role === "seller" && (
            <p className="text-sm text-muted-foreground flex items-start gap-1.5 rounded-xl bg-muted/60 p-3" data-testid="seller-note">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-secondary" /> {t("auth.sellerNote")}
            </p>
          )}
          {error && <p className="text-sm text-destructive font-medium" data-testid="register-error">{error}</p>}

          {/* Soft verification — optional ID / NIF */}
          <div className="rounded-2xl border-2 border-dashed border-border p-4" data-testid="verification-section">
            <p className="text-base font-bold text-secondary flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> {t("verify.docTitle")}</p>
            <p className="text-xs text-muted-foreground mt-1">{t("verify.docHint")}</p>
            <div className="mt-3">
              <Label className="text-secondary font-semibold">{t("verify.nifLabel")}</Label>
              <Input value={form.nif} onChange={(e) => set("nif", e.target.value)} className="mt-1.5 h-12" placeholder="NIF…" data-testid="register-nif" />
            </div>
            <div className="mt-3 flex flex-wrap gap-3 items-center">
              {form.documents.map((p) => (
                <div key={p} className="relative h-16 w-16 rounded-lg overflow-hidden border border-border">
                  <img src={resolvePhoto(p)} alt="" className="h-full w-full object-cover" />
                  <button type="button" onClick={() => set("documents", form.documents.filter((x) => x !== p))} className="absolute top-0 right-0 bg-red-600 rounded-bl p-0.5 text-white"><X className="h-3 w-3" /></button>
                </div>
              ))}
              <label className="h-16 w-24 rounded-lg border-2 border-dashed border-primary/40 flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-primary" data-testid="doc-upload-label">
                {uploadingDoc ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : <><Camera className="h-5 w-5 text-primary" /><span className="text-[10px] font-semibold text-primary">{t("verify.addDoc")}</span></>}
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files && uploadDoc(e.target.files[0])} data-testid="doc-input" />
              </label>
            </div>
            <a href={brand.support} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-full bg-success px-4 py-2 text-sm font-bold text-white hover:opacity-90" data-testid="verify-whatsapp">
              <MessageCircle className="h-4 w-4" /> {t("verify.viaWhatsapp")}
            </a>
          </div>

          <div className="rounded-2xl bg-accent/40 border border-border p-4 flex items-start gap-3" data-testid="verify-notice">
            <Clock className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-foreground">{t("verify.toVerify")}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{t("verify.notice")}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-2">
            <Button type="button" variant="outline" onClick={() => navigate("/")} className="h-16 rounded-2xl border-2 border-destructive text-destructive hover:bg-destructive/5 text-lg font-bold" data-testid="register-cancel">
              <X className="h-7 w-7 mr-2" strokeWidth={2.5} /> {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={loading} className="h-16 rounded-2xl bg-green-600 hover:bg-green-700 text-white text-lg font-bold" data-testid="register-submit">
              {loading ? <Loader2 className="h-7 w-7 mr-2 animate-spin" /> : <Check className="h-7 w-7 mr-2" strokeWidth={2.5} />}
              {t("common.validate")}
            </Button>
          </div>
          <p className="text-sm text-center text-muted-foreground">
            {t("auth.hasAccount")}{" "}
            <Link to="/connexion" className="text-primary font-semibold hover:underline" data-testid="go-login">{t("nav.login")}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
